import { Link } from "@tanstack/react-router";
import { Fragment, type ReactNode } from "react";
import type { NodeAncestor } from "../api.ts";
import { type Landing, useNodeDrop } from "../lib/dragMove.ts";
import styles from "./Breadcrumbs.module.css";
import { Icon } from "./Icon.tsx";

export function Breadcrumbs({ ancestors }: { ancestors: NodeAncestor[] }) {
  return (
    <nav className={styles.trail} aria-label="Location">
      <Crumb into={{ parentId: null, name: "Files" }}>
        <Icon name="files" />
        Files
      </Crumb>
      {ancestors.map((folder) => (
        <Fragment key={folder.id}>
          <Icon name="chevron" className={styles.chevron} />
          <Crumb into={{ parentId: folder.id, name: folder.name }}>{folder.name}</Crumb>
        </Fragment>
      ))}
    </nav>
  );
}

function Crumb({ into, children }: { into: Landing; children: ReactNode }) {
  const land = useNodeDrop(into);
  const link =
    into.parentId === null
      ? ({ to: "/" } as const)
      : ({ to: "/folders/$id", params: { id: into.parentId } } as const);
  return (
    <Link
      {...link}
      className={styles.crumb}
      data-dropping={land.over || undefined}
      onDragOver={land.dragOver}
      onDragLeave={land.dragLeave}
      onDrop={land.drop}
    >
      {children}
    </Link>
  );
}

/** The plain-text trail a row uses to say where it sits. */
export const locationOf = (ancestors: NodeAncestor[]): string =>
  ["Files", ...ancestors.map((folder) => folder.name)].join(" › ");
